<template>
    <div>
        <div class="d-flex">
            <v-spacer></v-spacer>
            <v-text-field
                class="my-2"
                style="max-width: 400px"
                v-model="search"
                placeholder="Search program name or instructor name"
                append-icon="mdi-magnify"
                label="Search"
                single-line
                hide-details
                solo
            ></v-text-field>
        </div>
        <v-data-table
            :custom-filter="filterPrograms"
            :search="search"
            :single-select="false"
            item-key="id"
            :show-select="can('programs.delete')"
            :headers="headers"
            :items="programs"
            :loading="loading"
            loading-text="Loading ... please wait"
            :loader-height="6"
            hide-default-footer
            disable-pagination
            @toggle-select-all="$emit('toggle-select-all', $event)"
            @item-selected="$emit('item-selected', $event)"
        >
            <template v-slot:item.session="{ item }">
                <program-session-select
                    :sessions="item.sessions"
                ></program-session-select>
            </template>
            <template v-slot:item.name="{ item }">
                <h3 v-if="can('programs.edit')">
                    <router-link
                        :to="{
                            name: 'Program Edit',
                            params: { id: item.id },
                        }"
                        >{{ item.name }}
                    </router-link>
                </h3>
                <h3 v-else>{{ item.name }}</h3>
                <div v-if="$_.has(item, 'address.street')">
                    <span
                        >{{ item.address.street }} {{ item.address.unit }}</span
                    ><br />
                    <span
                        >{{ item.address.city }}, {{ item.address.state }}
                        {{ item.address.zip }}</span
                    >
                </div>
            </template>
            <template v-slot:item.icon="{ item }">
                <v-avatar size="52" color="secondary" class="my-2">
                    <v-img
                        :src="$_.get(item, 'icon_filepath', '')"
                        max-height="48"
                        max-width="48"
                    ></v-img>
                </v-avatar>
            </template>
            <template v-slot:item.instructors="{ item }">
                <ul style="list-style: none" class="my-1">
                    <template v-for="(instructor, index) in item.instructors">
                        <li :key="index">
                            <v-avatar size="32" class="mr-2">
                                <v-img
                                    :src="instructor.profile_photo_url"
                                    max-height="26"
                                    max-width="26"
                                ></v-img>
                            </v-avatar>
                            <router-link
                                :to="{
                                    name: 'Instructor Dashboard',
                                    query: {
                                        instructorId: instructor.id,
                                        programId: item.id,
                                        sessionId: item.currentSessionId,
                                    },
                                }"
                                >{{ instructor.name }}</router-link
                            >
                        </li>
                    </template>
                </ul>
            </template>
            <template v-slot:item.details="{ item }">
                <table>
                    <tr v-for="(detail, index) in details" :key="index">
                        <td>
                            {{ detail.label }}
                        </td>
                        <td class="text-right" width="40">
                            {{ item[detail.key] }}
                        </td>
                    </tr>
                </table>
            </template>
            <template v-slot:item.actions="{ item }">
                <psi-action-drop-down
                    :heading="`Actions - ${$_.get(item, 'name')}`"
                    :items="filteredActions"
                    @action="$emit('action', item.id, $event)"
                ></psi-action-drop-down>
            </template>
        </v-data-table>
    </div>
</template>

<script>
export default {
    name: "program-list",
    components: {
        ProgramSessionSelect: () => import("./ProgramSessionSelect"),
    },
    props: {
        programs: {
            type: Array,
            required: true,
        },
        loading: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            search: "",
            filteredActions: [],
            details: [
                {
                    label: "Sessions",
                    key: "sessions_count",
                },
                {
                    label: "Instructors",
                    key: "instructors_count",
                },
                {
                    label: "Students",
                    key: "students_count",
                },
            ],
            actions: [
                {
                    name: "edit-program",
                    title: `Edit Program`,
                    icon: "mdi-home-edit",
                    permission: "programs.edit",
                },
                {
                    divider: true,
                    permission: "programs.edit",
                },
                {
                    name: "add-session",
                    title: "Add Session",
                    icon: "mdi-calendar-plus",
                    permission: "programs.edit",
                },
                {
                    name: "add-instructor",
                    title: "Add Instructor",
                    icon: "mdi-account-plus",
                    permission: "programs.edit",
                },
                {
                    divider: true,
                    permission: "students.edit-any",
                },
                {
                    name: "graduate-students",
                    title: "Graduate Students",
                    icon: "mdi-school",
                    permission: "students.edit-any",
                },
            ],
            headers: [
                {
                    text: "Program",
                    align: "start",
                    sortable: false,
                    value: "icon",
                    class: "text-subtitle-2 font-weight-medium primary--text text-uppercase",
                    cellClass: "py-2",
                },
                {
                    text: "Name",
                    align: "start",
                    sortable: true,
                    value: "name",
                    class: "text-subtitle-2 font-weight-medium primary--text text-uppercase",
                },

                {
                    text: "Details",
                    align: "start",
                    sortable: false,
                    value: "details",
                    class: "text-subtitle-2 font-weight-medium primary--text text-uppercase",
                },
                {
                    text: "Instructors",
                    align: "start",
                    sortable: false,
                    value: "instructors",
                    class: "text-subtitle-2 font-weight-medium primary--text text-uppercase",
                },
                {
                    text: "Actions",
                    align: "start",
                    sortable: false,
                    value: "actions",
                    class: "text-subtitle-2 font-weight-medium primary--text text-uppercase",
                },
            ],
        };
    },
    watch: {
        authUser: {
            immediate: true,
            handler() {
                this.filteredActions = this.actions.filter((action) =>
                    this.can(action.permission)
                );
            },
        },
    },

    methods: {
        // eslint-disable-next-line no-unused-vars
        filterPrograms(value, search, item) {
            return (
                search === undefined ||
                search === null ||
                item.name.toLowerCase().includes(search.toLowerCase()) ||
                item.instructors.some((ins) =>
                    ins.name.toLowerCase().includes(search.toLowerCase())
                )
            );
        },
    },
};
</script>

<style lang="scss" scoped>
</style>